import { Injectable, NgZone } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { UsuarioLogado } from '../models/usuario-logado.model';
import { Router } from '@angular/router';
import { EmpresaResponse } from '../models/response/empresa-response.model';
import { UsuarioResponse } from '../models/response/usuario-response.model';
import { LoginResponse } from '../models/response/login-response.model';
import { BehaviorSubject, Observable, finalize, firstValueFrom, from, of, shareReplay, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

// Identifica o ÓTICOM Web nas chamadas de refresh/logout. O refresh em si
// trafega apenas no cookie HttpOnly __Secure-oticom_web_refresh, que o
// JavaScript não lê nem manipula.
export const HEADER_APLICACAO = 'X-Aplicacao';
export const APLICACAO_OTICOM_WEB = 'OTICOM';

// Nomes exclusivos do Web, para não interferir na sessão do ÓTICOM Lab.
const CANAL_SESSAO = 'oticom_web_sessao';
const LOCK_SESSAO = 'oticom_web_sessao';

type MensagemSessao =
  | { tipo: 'logout' }
  | { tipo: 'token'; token: string };

/** A sessão terminou (logout/troca de sessão) enquanto a renovação aguardava. */
export class SessaoEncerradaError extends Error { }

/** Segundos do header Retry-After (número ou data HTTP); 0 se ausente/inválido. */
export function segundosRetryAfter(erro: HttpErrorResponse): number {
  const valor = erro?.headers?.get('Retry-After');
  if (!valor) return 0;
  const segundos = Number(valor);
  if (!isNaN(segundos)) return Math.max(0, Math.ceil(segundos));
  const data = Date.parse(valor);
  return isNaN(data) ? 0 : Math.max(0, Math.ceil((data - Date.now()) / 1000));
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // Chave antiga do access token; o JWT agora fica só em memória.
  private weblab_token_legado: string = 'weblab_token';
  private weblab_usuario: string = 'weblab_usuario';
  private weblab_empresa: string = 'weblab_empresa';

  usuario: string; // Vem da tela de login;

  private tokenExpiradoSubject = new BehaviorSubject<boolean>(false);

  // Access JWT somente em memória; o refresh persiste apenas no cookie HttpOnly.
  private tokenMemoria: string | null = null;
  private refreshEmAndamento$: Observable<string> | null = null;
  // 429 no refresh/login: nenhuma nova tentativa antes do Retry-After.
  private refreshBloqueado: { ate: number; erro: HttpErrorResponse } | null = null;
  loginBloqueadoAte = 0;
  // Mensagem exibida no login após encerramento da autenticação.
  mensagemEncerramento = '';
  // Muda a cada login/logout; respostas de refresh de uma sessão anterior são descartadas.
  private geracaoSessao = 0;
  private canal: BroadcastChannel | null = null;
  private httpSemInterceptor: HttpClient;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    httpBackend: HttpBackend) {
    this.httpSemInterceptor = new HttpClient(httpBackend);
    localStorage.removeItem(this.weblab_token_legado);
    this.iniciarCanalSessao();
  }

  /**
   * Entrada em rota protegida: JWT em memória ou contexto recuperável (usuário
   * do Web salvo). Sem refresh preventivo: a primeira consulta protegida que
   * receber 401 renova pelo cookie e repete com o novo JWT.
   */
  canActivate(): boolean {
    return !!this.tokenAcesso || !!this.obterUsuarioLogado;
  }

  salvarToken(token: LoginResponse) {
    this.geracaoSessao++;
    this.definirToken(token?.dados?.token_jwt || null);
  }

  salvarUsuarioLogado(usuario: string, usuarioResponse: UsuarioResponse) {
    localStorage.setItem(this.weblab_usuario, JSON.stringify({
      nome: usuarioResponse.usuario_oticomlab,
      usuario: usuario,
      empresa: usuarioResponse.empresa_usuario_oticomlab
    } as UsuarioLogado));
  }

  salvarEmpresa(empresa: EmpresaResponse) {
    localStorage.setItem(this.weblab_empresa, JSON.stringify(empresa));
  }

  /**
   * Recuperação de um 401 de rota protegida. Uma única renovação é
   * compartilhada entre chamadas e, via Web Locks, entre abas do Web. Se o
   * token usado pela chamada já foi substituído, devolve o atual sem renovar.
   *
   * Erros: SessaoEncerradaError, 401 ou 400 de cookie ausente/inválido (ex.:
   * sessão só com o cookie antigo) encerram a sessão; demais (429, 503, rede)
   * seguem para tratamento próprio, sem nova tentativa.
   */
  renovarToken(tokenUsado: string | null): Observable<string> {
    const atual = this.tokenAcesso;
    if (atual && atual !== tokenUsado) return of(atual);

    const bloqueio = this.refreshBloqueado;
    if (bloqueio && Date.now() < bloqueio.ate) return throwError(() => bloqueio.erro);

    if (!this.refreshEmAndamento$) {
      const geracao = this.geracaoSessao;

      this.refreshEmAndamento$ = from(this.comLockSessao(async () => {
        const tokenNoInicio = this.tokenAcesso;
        if (tokenNoInicio && tokenNoInicio !== tokenUsado) return tokenNoInicio; // outra aba já renovou
        if (!this.sessaoVigente(geracao)) throw new SessaoEncerradaError();

        let resposta: LoginResponse;
        try {
          resposta = await firstValueFrom(this.httpSemInterceptor.post<LoginResponse>(
            `${environment.api_url}/token/refresh`, null, { headers: this.headersSessao(), withCredentials: true }));
        } catch (erro) {
          if (erro instanceof HttpErrorResponse && erro.status === 429) {
            const segundos = segundosRetryAfter(erro);
            if (segundos) this.refreshBloqueado = { ate: Date.now() + segundos * 1000, erro };
          }
          if (this.isSessaoEncerrada(erro) && this.sessaoVigente(geracao)) this.definirToken(null);
          throw erro;
        }

        const novoToken = resposta?.dados?.token_jwt;
        if (!novoToken || !this.sessaoVigente(geracao)) throw new SessaoEncerradaError();

        this.definirToken(novoToken);
        this.publicar({ tipo: 'token', token: novoToken });
        return novoToken;
      })).pipe(
        finalize(() => this.refreshEmAndamento$ = null),
        shareReplay(1)
      );
    }

    return this.refreshEmAndamento$;
  }

  isSessaoEncerrada(erro: any): boolean {
    if (erro instanceof SessaoEncerradaError) return true;
    if (!(erro instanceof HttpErrorResponse)) return false;
    return erro.status === 401
      || (erro.status === 400 && ['REFRESH_AUSENTE', 'REFRESH_INVALIDO'].includes(erro.error?.erro?.codigo));
  }

  /**
   * Autenticação terminal (refresh recusado ou repetição com 401): encerra a
   * sessão do Web nesta e nas demais abas e segue para login/licença, sem novo
   * ciclo de refresh. Idempotente para chamadas concorrentes.
   */
  encerrarAutenticacao() {
    if (!this.tokenAcesso && !this.obterUsuarioLogado) return;

    this.geracaoSessao++;
    this.mensagemEncerramento = 'Sua sessão expirou. Faça login novamente.';
    this.publicar({ tipo: 'logout' });
    this.encerrarSessaoLocal();
  }

  logout() {
    this.geracaoSessao++;

    // Sob o mesmo lock da renovação: aguarda refresh em andamento (em qualquer
    // aba do Web) para revogar o cookie mais recente.
    this.comLockSessao(() => firstValueFrom(this.httpSemInterceptor.post(
      `${environment.api_url}/token/logout`, null, { headers: this.headersSessao(), withCredentials: true }))
    ).catch(() => { });

    this.publicar({ tipo: 'logout' });
    this.encerrarSessaoLocal();
  }

  limparCacheEmpersa() {
    localStorage.removeItem(this.weblab_empresa);
  }

  tokenExpiradoEmit(value: boolean) {
    this.tokenExpiradoSubject.next(value);
  }

  obterTokenExpirado() {
    return this.tokenExpiradoSubject;
  }

  get obterUsuarioLogado(): UsuarioLogado {
    return localStorage.getItem(this.weblab_usuario)
      ? JSON.parse(localStorage.getItem(this.weblab_usuario) as string)
      : null;
  }
  get obterEmailUsuarioLogado(): string | null {
    return localStorage.getItem(this.weblab_usuario)
      ? (JSON.parse(localStorage.getItem(this.weblab_usuario) as string) as UsuarioLogado).usuario
      : null;
  }
  get obterTokenUsuario(): string {
    return this.tokenAcesso ?? 'null';
  }

  get obterEmpresa(): EmpresaResponse {
    return localStorage.getItem(this.weblab_empresa)
      ? JSON.parse(localStorage.getItem(this.weblab_empresa) as string)
      : null;
  }

  /** Mesma sessão desta aba e ainda logada (outra aba pode ter saído). */
  private sessaoVigente(geracao: number): boolean {
    return geracao === this.geracaoSessao && !!this.obterUsuarioLogado;
  }

  private headersSessao(): HttpHeaders {
    return new HttpHeaders({ [HEADER_APLICACAO]: APLICACAO_OTICOM_WEB });
  }

  private comLockSessao<T>(fn: () => Promise<T>): Promise<T> {
    const locks = (navigator as any).locks;
    return locks?.request ? locks.request(LOCK_SESSAO, fn) : fn();
  }

  private get tokenAcesso(): string | null {
    return this.tokenMemoria;
  }

  private definirToken(token: string | null) {
    this.tokenMemoria = token;
  }

  private encerrarSessaoLocal() {
    const empresa = this.obterEmpresa;

    this.definirToken(null);
    localStorage.removeItem(this.weblab_usuario);

    this.tokenExpiradoSubject.next(false);
    this.router.navigate(empresa?.token_acesso ? ['/', empresa.token_acesso] : ['/']);
  }

  private iniciarCanalSessao() {
    if (typeof BroadcastChannel === 'undefined') return;

    this.canal = new BroadcastChannel(CANAL_SESSAO);
    this.canal.onmessage = (evento: MessageEvent<MensagemSessao>) => {
      this.ngZone.run(() => {
        const mensagem = evento.data;
        if (mensagem?.tipo === 'logout') {
          this.geracaoSessao++;
          this.encerrarSessaoLocal();
        }
        // JWT renovado por outra aba do Web: apenas em memória, sem persistir.
        if (mensagem?.tipo === 'token' && this.obterUsuarioLogado) this.definirToken(mensagem.token);
      });
    };
  }

  private publicar(mensagem: MensagemSessao) {
    this.canal?.postMessage(mensagem);
  }
}
